/**
 * @file Resolves an operation ID to its HTTP method, form schema and index item schema.
 */
import operations from "../operations.json";
import httpMethods from "../httpMethods.js";
import servicesMap from "../services/index.js";
import extendSchema from "./extendSchema";
import type { SchemaInput } from "./extendSchema";

export type IndexItemSchema = Record<string, unknown>;

export type Operation = {
  id: string;
  method: string;
  schema?: SchemaInput[];
  isCreate: boolean;
  isUpdate: boolean;
  getSchema?: (inputs?: SchemaInput[]) => SchemaInput[];
  getIndexItemSchema: () => IndexItemSchema;
};

const CREATE_OPERATION_MARKERS = [
  "/Send",
  "/Create",
  "/Import",
  "/Connect",
  "/ChangeMyIdentityEmail",
  "/ConfirmMyIdentityEmail",
];

const FORM_INPUTS_POSTFIX = "_FORM_INPUTS";
const ITEM_SCHEMA_POSTFIX = "_ITEM_SCHEMA";

const methodsMap: Record<string, string> = httpMethods;
const componentsMap: Record<string, unknown> = servicesMap;

/**
 * Looks up an allowlisted operation and builds its method, schema and index item schema accessors.
 */
const getOperation = (operationId: string): Operation => {
  const isSupported = operations.includes(operationId);

  if (!isSupported) {
    const message =
      `Operation "${operationId}" is missing. ` +
      `Add "${operationId}" to "src/api/operations.json" and run "npm run update".`;

    throw new Error(message);
  }

  const isCreate = CREATE_OPERATION_MARKERS.some((marker) => operationId.includes(marker));
  const isUpdate = !isCreate;

  const method = methodsMap[operationId];

  const operationKey = operationId.replace("/", "_").toUpperCase();
  const formInputsKey = `${operationKey}${FORM_INPUTS_POSTFIX}`;
  const tableColumnsKey = `${operationKey}${ITEM_SCHEMA_POSTFIX}`;

  const formInputs = componentsMap[formInputsKey] as SchemaInput[] | undefined;

  /**
   * Merges the operation's default form inputs with the caller's custom inputs.
   */
  const getSchema = (inputs: SchemaInput[] = []): SchemaInput[] => {
    const schema = extendSchema(formInputs, inputs);

    return schema;
  };

  /**
   * Returns the operation's index item schema, or throws when it has none.
   */
  const getIndexItemSchema = (): IndexItemSchema => {
    const tableColumns = componentsMap[tableColumnsKey] as IndexItemSchema | undefined;

    if (!tableColumns) {
      const message = `Operation "${operationId}" has no index item schema.`;

      throw new Error(message);
    }

    return tableColumns;
  };

  const operation: Operation = {
    id: operationId,
    method,
    isCreate,
    isUpdate,
    getIndexItemSchema,
  };

  const hasFormInputs = !!formInputs;

  if (hasFormInputs) {
    operation.schema = formInputs;
    operation.getSchema = getSchema;
  }

  return operation;
};

export default getOperation;
